import { CartSupplement } from '../models/supplement.model';

/**
 * Order-line identity and money, once.
 *
 * Mirrors iOS `SupplementPricing` and the backend's `OrderSupplementService`. All three must agree
 * on the key format: the server merges order lines by it, so a client that builds it differently
 * gets its lines merged into each other or duplicated.
 *
 * Pure by design — no Angular, no storage, no DOM. Identity is asked for on every keystroke of a
 * stepper and from three different services; anything injectable here would drag them together.
 */

/**
 * Identity of an order line: the product PLUS its exact add-on selection.
 *
 * Keying on product id alone merges "Pizza + cheese" into "Pizza + bacon" — two genuinely
 * different lines, one of which then silently overwrites the other on the next push.
 *
 * Ids are sorted so selection ORDER never changes the key: a guest who ticks bacon then cheese
 * must land on the same line as one who ticked cheese then bacon. The sort is the default
 * lexicographic one on the already-formatted `"<id>x<qty>"` strings, which is what Swift's
 * `sorted()` does on the same strings — a numeric comparator here would order "10x1" before "9x1"
 * and disagree with iOS and the backend for any venue with ten or more add-ons.
 *
 * A product with no add-ons yields `"<productId>|"`, one-to-one with the product id, so every
 * WooCommerce line groups exactly as it did before add-ons existed.
 */
export function cartLineKey(
  productId: number | null | undefined,
  supplements?: CartSupplement[] | null
): string {
  const entries = (supplements ?? [])
    .map(supplement => `${supplement.id}x${supplement.quantity > 0 ? supplement.quantity : 1}`)
    .sort();
  return `${productId ?? -1}|${entries.join(',')}`;
}

/**
 * Add-on cost for ONE unit of the product: each add-on's price times its own quantity.
 *
 * Split out from the line total because the product sheet prices a single unit while the cart
 * prices the line, and computing "price of one with these add-ons" by dividing the line total is
 * how rounding drift gets in.
 */
export function addonUnitTotal(supplements?: CartSupplement[] | null): number {
  return (supplements ?? []).reduce(
    (sum, supplement) => sum + supplement.price * (supplement.quantity > 0 ? supplement.quantity : 1),
    0
  );
}

/**
 * Full money for a cart line, add-ons included.
 *
 * Add-ons multiply with the product, they do not sit beside it: three cheese pizzas cost three
 * cheeses. With no add-ons this is exactly `basePrice * quantity`, byte-for-byte what the payload
 * carried before this feature.
 */
export function cartLineTotal(
  basePrice: number,
  quantity: number,
  supplements?: CartSupplement[] | null
): number {
  return (basePrice + addonUnitTotal(supplements)) * quantity;
}

/**
 * Whether two selections are the same line.
 *
 * Expressed through the key rather than by comparing arrays, so there is ONE definition of
 * sameness: an element-wise comparison would answer differently for a reordered selection and
 * reintroduce the duplicate-line bug the sort in `cartLineKey` exists to prevent. The product id
 * is irrelevant to the question, hence the constant.
 */
export function hasSameSupplements(
  a?: CartSupplement[] | null,
  b?: CartSupplement[] | null
): boolean {
  return cartLineKey(0, a) === cartLineKey(0, b);
}
